import { useParams } from 'react-router-dom';

import { useGetDoc } from '@/api/queries/useGetDoc';
import { DocumentViewer } from '@/new/components/doc-viewer/document-viewer';
import { Drawer } from '@/new/components/drawers/drawer';
import { Icon } from '@/new/components/icon';
import { DrawerErrorState } from '@/new/components/reference-content/drawer-error-state';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { getFileIconClass, getFileTypeLabel } from '@/new/lib/file-type-display';
import { cn } from '@/new/lib/utils';
import { BoundingBoxWithId } from '@/types';

const NO_HIGHLIGHTS: BoundingBoxWithId[] = [];

interface FilePreviewDrawerProps {
  docId: string | null;
  onClose: () => void;
}

export function FilePreviewDrawer({ docId, onClose }: FilePreviewDrawerProps) {
  const { matterId } = useParams();
  const { data: doc, isLoading, error } = useGetDoc(matterId, docId);

  const extension = doc?.file?.extension ?? null;
  const fileTypeLabel = getFileTypeLabel(extension);

  return (
    <Drawer isOpen={docId !== null} onClose={onClose} hideCloseButton={true}>
      <div className="flex h-full flex-col">
        <div className="flex shrink-0 items-center gap-2 border-b px-2.5 py-2">
          <Icon className={cn('size-5', getFileIconClass(extension))} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-gray-900">{doc?.file?.name}</p>
            {fileTypeLabel && <p className="text-[11px] text-gray-500">{fileTypeLabel}</p>}
          </div>
          <div className="-mr-0.5 flex items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="p-1 px-1.5 hover:bg-gray-100"
                  onClick={onClose}
                  aria-label="Close preview"
                >
                  <Icon className="lucide--panel-right-close size-3.5 text-gray-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Close preview</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="relative flex-1 overflow-hidden">
          {error ? (
            <DrawerErrorState label="document" />
          ) : isLoading || !doc || !docId ? (
            <div className="flex h-full w-full items-center justify-center">
              <Icon className="lucide--loader-2 animate-spin duration-700" />
            </div>
          ) : (
            <DocumentViewer
              matterId={matterId}
              fileExtension={doc.file?.extension}
              docId={docId}
              highlights={NO_HIGHLIGHTS}
              hideFeatureButtons={true}
              hideSnippingTool={true}
              hideHighlightedDownload={true}
              hideSearch={true}
              fileName={doc.file?.name}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
}
